/**
 * CSV helpers for question bank import / export / sample.
 */

export const QUESTION_CSV_HEADERS = [
  "question_text_en",
  "question_text_ar",
  "question_text_ur",
  "question_type",
  "difficulty",
  "option_1_en",
  "option_2_en",
  "option_3_en",
  "option_4_en",
  "correct_answers",
  "default_marks",
  "tags",
  "explanation_en",
];

export const SAMPLE_QUESTION_CSV_ROWS = [
  {
    question_text_en: "How many pillars of Islam are there?",
    question_text_ar: "",
    question_text_ur: "",
    question_type: "mcq",
    difficulty: "easy",
    option_1_en: "Three",
    option_2_en: "Four",
    option_3_en: "Five",
    option_4_en: "Six",
    correct_answers: "2",
    default_marks: "1",
    tags: "islam,pillars",
    explanation_en: "There are five pillars of Islam.",
  },
  {
    question_text_en: "Is Salah one of the five pillars of Islam?",
    question_text_ar: "",
    question_text_ur: "",
    question_type: "true_false",
    difficulty: "easy",
    option_1_en: "True",
    option_2_en: "False",
    option_3_en: "",
    option_4_en: "",
    correct_answers: "0",
    default_marks: "1",
    tags: "salah",
    explanation_en: "",
  },
];

function escapeCsvCell(value) {
  const str = value == null ? "" : String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function rowsToCsv(headers, rows) {
  const lines = [headers.join(",")];
  for (const row of rows) {
    lines.push(headers.map((h) => escapeCsvCell(row[h])).join(","));
  }
  return lines.join("\n") + "\n";
}

export function downloadCsv(filename, csvText) {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function getSampleQuestionsCsv() {
  return rowsToCsv(QUESTION_CSV_HEADERS, SAMPLE_QUESTION_CSV_ROWS);
}

/**
 * Minimal RFC4180-style CSV parser (supports quoted fields).
 * @returns {{ headers: string[], rows: Record<string, string>[] }}
 */
export function parseCsv(text) {
  const normalized = String(text || "").replace(/^\uFEFF/, "");
  const rows = [];
  let row = [];
  let cell = "";
  let inQuotes = false;

  for (let i = 0; i < normalized.length; i++) {
    const ch = normalized[i];
    const next = normalized[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(cell);
      cell = "";
    } else if (ch === "\n") {
      row.push(cell);
      cell = "";
      if (row.some((c) => String(c).trim() !== "")) rows.push(row);
      row = [];
    } else if (ch === "\r") {
      // ignore CR (handle CRLF via following \n)
    } else {
      cell += ch;
    }
  }

  if (cell.length || row.length) {
    row.push(cell);
    if (row.some((c) => String(c).trim() !== "")) rows.push(row);
  }

  if (rows.length === 0) {
    return { headers: [], rows: [] };
  }

  const headers = rows[0].map((h) => String(h).trim());
  const dataRows = rows.slice(1).map((cells) => {
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = cells[idx] != null ? String(cells[idx]).trim() : "";
    });
    return obj;
  });

  return { headers, rows: dataRows };
}

function localized(en, ar, ur) {
  const out = {};
  if (en) out.en = en;
  if (ar) out.ar = ar;
  if (ur) out.ur = ur;
  return out;
}

/**
 * Convert a CSV row into an API question payload.
 * @throws {Error} validation message
 */
export function csvRowToQuestion(row, index) {
  const line = index + 2; // header is line 1
  const textEn = row.question_text_en?.trim();
  if (!textEn) {
    throw new Error(`Row ${line}: question_text_en is required`);
  }

  const question_type = (row.question_type || "mcq").trim().toLowerCase();
  const allowedTypes = ["mcq", "multiple_select", "true_false"];
  if (!allowedTypes.includes(question_type)) {
    throw new Error(
      `Row ${line}: question_type must be one of ${allowedTypes.join(", ")}`
    );
  }

  const optionTexts = [
    row.option_1_en,
    row.option_2_en,
    row.option_3_en,
    row.option_4_en,
  ]
    .map((t) => (t || "").trim())
    .filter(Boolean);

  if (question_type === "true_false") {
    if (optionTexts.length === 0) {
      optionTexts.push("True", "False");
    }
    if (optionTexts.length !== 2) {
      throw new Error(
        `Row ${line}: true_false needs exactly 2 options (or leave blank for True/False)`
      );
    }
  } else if (optionTexts.length < 2) {
    throw new Error(`Row ${line}: at least 2 options are required`);
  }

  const correctRaw = (row.correct_answers || "").trim();
  if (!correctRaw) {
    throw new Error(
      `Row ${line}: correct_answers is required (0-based indexes, e.g. 0 or 0,2)`
    );
  }
  const correct_answers = correctRaw
    .split(/[|;,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number(s));

  if (correct_answers.some((n) => !Number.isInteger(n) || n < 0)) {
    throw new Error(
      `Row ${line}: correct_answers must be 0-based integer indexes`
    );
  }

  const tags = (row.tags || "")
    .split(/[|;,]/)
    .map((t) => t.trim())
    .filter(Boolean);

  const marks = Number(row.default_marks);
  const default_marks = Number.isFinite(marks) && marks > 0 ? marks : 1;

  const explanationEn = (row.explanation_en || "").trim();

  return {
    question_text: localized(
      textEn,
      row.question_text_ar?.trim(),
      row.question_text_ur?.trim()
    ),
    question_type,
    difficulty: (row.difficulty || "easy").trim().toLowerCase() || "easy",
    options: optionTexts.map((en) => ({ en })),
    correct_answers,
    default_marks,
    tags,
    ...(explanationEn ? { explanation: { en: explanationEn } } : {}),
  };
}

export function questionsToCsvRows(questions) {
  return (questions || []).map((q) => {
    const opts = Array.isArray(q.options) ? q.options : [];
    const opt = (i) => {
      const o = opts[i];
      if (!o) return "";
      return typeof o === "string" ? o : o.en || "";
    };
    return {
      question_text_en: q.question_text?.en || "",
      question_text_ar: q.question_text?.ar || "",
      question_text_ur: q.question_text?.ur || "",
      question_type: q.question_type || "mcq",
      difficulty: q.difficulty || "easy",
      option_1_en: opt(0),
      option_2_en: opt(1),
      option_3_en: opt(2),
      option_4_en: opt(3),
      correct_answers: Array.isArray(q.correct_answers)
        ? q.correct_answers.join(",")
        : "",
      default_marks: q.default_marks ?? 1,
      tags: Array.isArray(q.tags) ? q.tags.filter(Boolean).join(",") : "",
      explanation_en: q.explanation?.en || "",
    };
  });
}

export function exportQuestionsCsv(questions, filename = "questions-export.csv") {
  const csv = rowsToCsv(QUESTION_CSV_HEADERS, questionsToCsvRows(questions));
  downloadCsv(filename, csv);
}
